import { getConfigValue } from '@/lib/config'

const REFERRAL_STORAGE_KEY = 'aelo_referral_attribution_v1'
const REFERRAL_MAX_AGE_MS = 30 * 24 * 60 * 60 * 1000
const REFERRAL_CODE_RE = /^[A-Z0-9]{6,16}$/

interface StoredReferralAttribution {
  code: string
  capturedAt: number
}

export function normalizeReferralCode(raw: unknown): string | null {
  if (typeof raw !== 'string') return null
  const normalized = raw.trim().toUpperCase().replace(/[\s-]+/g, '')
  return REFERRAL_CODE_RE.test(normalized) ? normalized : null
}

function readStoredReferral(now = Date.now()): StoredReferralAttribution | null {
  try {
    const raw = window.localStorage.getItem(REFERRAL_STORAGE_KEY)
    if (!raw) return null
    const stored = JSON.parse(raw) as Partial<StoredReferralAttribution>
    const code = normalizeReferralCode(stored.code)
    const capturedAt = Number(stored.capturedAt)
    if (!code || !Number.isFinite(capturedAt) || capturedAt <= 0 || now - capturedAt > REFERRAL_MAX_AGE_MS) {
      window.localStorage.removeItem(REFERRAL_STORAGE_KEY)
      return null
    }
    return { code, capturedAt }
  } catch {
    return null
  }
}

export function captureReferralCode(raw: unknown, capturedAt = Date.now()): string | null {
  const code = normalizeReferralCode(raw)
  if (!code) return null
  try {
    window.localStorage.setItem(REFERRAL_STORAGE_KEY, JSON.stringify({ code, capturedAt }))
  } catch {
    // Query-string propagation remains available when storage is disabled.
  }
  return code
}

export function getReferralCodeFromLocation(): string | null {
  const params = new URLSearchParams(window.location.search)
  const queryCode = normalizeReferralCode(params.get('aelo_ref') || params.get('code') || params.get('invite'))
  if (queryCode) return queryCode

  const invitePathMatch = window.location.pathname.match(/^\/invite\/([^/?#]+)/i)
  return normalizeReferralCode(invitePathMatch?.[1] ?? null)
}

export function resolveReferralCode(explicitCode?: string | null): string | null {
  const direct = normalizeReferralCode(explicitCode) || getReferralCodeFromLocation()
  if (direct) return captureReferralCode(direct)
  return readStoredReferral()?.code ?? null
}

export function buildShopifyMembershipUrl(referralCode?: string | null): string {
  const configured = getConfigValue('SHOPIFY_MEMBERSHIP_PRODUCT_URL')
  const url = new URL(configured, window.location.origin)
  const code = normalizeReferralCode(referralCode)
  if (code) {
    url.searchParams.set('aelo_ref', code)
  }
  return url.toString()
}

export function buildShopifyStoreUrl(referralCode?: string | null): string {
  const configured = `${getConfigValue('SHOPIFY_STORE_URL')}/collections/all`
  const url = new URL(configured, window.location.origin)
  const code = normalizeReferralCode(referralCode)
  if (code) {
    url.searchParams.set('aelo_ref', code)
  }
  return url.toString()
}

export function buildShopifyUrl(path = '/', referralCode?: string | null): string {
  const configured = getConfigValue('SHOPIFY_STORE_URL')
  const base = configured.endsWith('/') ? configured : `${configured}/`
  const normalizedPath = path.startsWith('/') ? path.slice(1) : path
  const url = new URL(normalizedPath, base)
  const code = normalizeReferralCode(referralCode)
  if (code) {
    url.searchParams.set('aelo_ref', code)
  }
  return url.toString()
}
